import { ChevronDown, ExternalLink, Info, Link2, Plus, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import useAtelierCreator from '../../stores/useAtelierCreator';
 
const tokens = (dm) => ({
  textSub: dm ? 'text-zinc-400' : 'text-zinc-500',
  textMuted: dm ? 'text-zinc-600' : 'text-zinc-400',
  input: dm
    ? 'bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-slate-400/60 focus:ring-1 focus:ring-slate-400/20'
    : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-100 shadow-sm',
});

function FieldLabel({ label, hint, required, darkMode }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5">
        <label className={`text-[11px] font-bold uppercase tracking-widest ${darkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{label}</label>
        {required && <span className="text-[9px] font-bold text-red-400 border border-red-400/30 px-1 rounded-sm">requis</span>}
      </div>
      {hint && <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{hint}</p>}
    </div>
  );
}

function WorkspaceCard({ title, subtitle, icon: Icon, accent = 'slate', required = false, children, darkMode, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const dm = darkMode;
  const accentColors = { slate: { border: 'border-l-slate-400', icon: 'text-slate-500' }, emerald: { border: 'border-l-emerald-400', icon: 'text-emerald-500' } };
  const ac = accentColors[accent] || accentColors.slate;

  return (
    <div className={`rounded-lg border transition-all overflow-hidden ${open ? `${ac.border} border-l-2 border-y ${dm ? 'border-white/8 bg-zinc-900/40' : 'border-zinc-200 bg-white shadow-sm'}` : `border-l-2 border-l-transparent border-y ${dm ? 'border-white/5' : 'border-zinc-100'}`}`}>
      <button type="button" onClick={() => setOpen(v => !v)} className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${dm ? 'hover:bg-white/3' : 'hover:bg-zinc-50/70'}`}>
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-1.5 rounded-md ${dm ? 'bg-zinc-800' : 'bg-zinc-100'}`}><Icon size={14} className={open ? ac.icon : dm ? 'text-zinc-500' : 'text-zinc-400'} /></div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-[13px] font-semibold ${open ? (dm ? 'text-white' : 'text-zinc-900') : (dm ? 'text-zinc-500' : 'text-zinc-500')}`}>{title}</span>
              {required && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${open ? 'text-red-400 border-red-400/30 bg-red-400/5' : 'text-zinc-500 border-zinc-600'}`}>REQUIS</span>}
            </div>
            {subtitle && open && <p className={`text-[11px] mt-0.5 ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>{subtitle}</p>}
          </div>
        </div>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${dm ? 'text-zinc-600' : 'text-zinc-400'} ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className={`px-5 pb-5 border-t ${dm ? 'border-white/5' : 'border-zinc-100'}`}><div className="pt-4">{children}</div></div>}
    </div>
  );
}

const RESOURCE_TYPES = ['link', 'pdf', 'video', 'tool'];

export default function Section3_Ressources({ darkMode }) {
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-lg px-3 py-2.5 text-[13px] w-full`;

  const { formData, errors, addResource, updateResource, removeResource, saveMappings, interfaceMode } = useAtelierCreator();
  const isSimple = interfaceMode === 'simplifie';
  const resources = formData.resources || [];

  return (
    <div className="space-y-4 animate-in fade-in duration-500">

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
            {isSimple ? 'Ressources de l\'atelier' : 'Bibliothèque & outils'}
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            {isSimple ? 'Liens, documents et outils complémentaires' : 'Configuration des ressources externes'}
          </p>
        </div>
        <div className={`text-[10px] font-mono px-2 py-1 rounded border ${dm ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
          {isSimple ? 'MODE SIMPLIFIÉ' : 'MODE EXPERT'}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        
        {/* Colonne gauche — Ressources */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <WorkspaceCard title="Ressources externes" subtitle="Liens, PDF, vidéos" icon={Link2} accent="slate" required darkMode={dm} defaultOpen>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className={`text-[11px] font-semibold ${tk.textSub}`}>Documents ({resources.length})</span>
                <button type="button" onClick={addResource} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-md border border-slate-500/30 text-slate-400 hover:bg-slate-500/10"><Plus size={11} /> Ajouter</button>
              </div>
              {resources.map((res, idx) => (
                <div key={idx} className={`rounded-lg border p-4 ${dm ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-zinc-50'}`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className={`text-[10px] font-mono uppercase ${tk.textMuted}`}>Ressource_{String(idx + 1).padStart(2, '0')}</span>
                    <button onClick={() => removeResource(idx)} className={`p-1 transition-colors ${dm ? 'text-zinc-500 hover:text-red-400' : 'text-zinc-400 hover:text-red-500'}`}><Trash2 size={12} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input value={res.title || ''} onChange={e => updateResource(idx, 'title', e.target.value)} className={inputCls} placeholder="Titre de la ressource" />
                    <select value={res.type || 'link'} onChange={e => updateResource(idx, 'type', e.target.value)} className={inputCls}>
                      {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <input value={res.type === 'pdf' ? res.filename || '' : res.url || ''} onChange={e => { const field = res.type === 'pdf' ? 'filename' : 'url'; updateResource(idx, field, e.target.value); }} className={`${inputCls} flex-1`} placeholder={res.type === 'pdf' ? 'fichier.pdf' : 'https://...'} />
                    {res.type !== 'pdf' && res.url && (
                      <a href={res.url} target="_blank" rel="noopener noreferrer" className={`px-3 rounded-lg border flex items-center transition-colors ${dm ? 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white' : 'bg-zinc-100 border-zinc-200 text-zinc-500 hover:text-zinc-700'}`}>
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {resources.length === 0 && (
                <div className={`p-8 text-center rounded-lg border border-dashed ${dm ? 'border-white/5' : 'border-zinc-200'}`}>
                  <p className={`text-[11px] font-mono uppercase ${tk.textMuted}`}>Aucune ressource — cliquez sur "Ajouter"</p>
                </div>
              )}
            </div>
          </WorkspaceCard>
        </div>

        {/* Colonne droite — Synchronisation */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          <WorkspaceCard title="Synchronisation" subtitle="Sauvegarde des mappings" icon={Save} accent="emerald" darkMode={dm} defaultOpen>
            <div className="space-y-4">
              <p className={`text-[11px] ${tk.textSub}`}>Sauvegardez les associations entre templates et ateliers.</p>
              <button onClick={saveMappings} className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-black uppercase tracking-wider rounded-lg transition-all">
                <Save size={14} /> Synchroniser les mappings
              </button>
            </div>
          </WorkspaceCard>
        </div>
      </div>

      {isSimple && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${dm ? 'bg-slate-500/5 border-slate-500/20' : 'bg-slate-50 border-slate-200'}`}>
          <Info size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
          <p className={`text-[12px] leading-relaxed ${dm ? 'text-slate-300/80' : 'text-slate-700'}`}>
            <strong>Conseil :</strong> Ajoutez des liens vers des ressources externes (vidéos, articles) pour enrichir l'atelier.
          </p>
        </div>
      )}
    </div>
  );
}